/**
 * Public Roadmap — items the admin has flagged with `public_roadmap=true`.
 * Visible to any authenticated user in the tenant. Read-only.
 */
import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import AppShell from "../components/AppShell";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Lightbulb, MagicWand, Bug, Megaphone } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUS_LABEL = {
  new: { label: "Under consideration", color: "bg-zinc-100 text-zinc-700" },
  triaged: { label: "Planned", color: "bg-indigo-50 text-indigo-700 border border-indigo-200" },
  in_progress: { label: "Building now", color: "bg-amber-50 text-amber-800 border border-amber-200" },
  resolved: { label: "Shipped", color: "bg-emerald-50 text-emerald-800 border border-emerald-200" },
  verified: { label: "Shipped", color: "bg-emerald-50 text-emerald-800 border border-emerald-200" },
};

const TYPE_ICONS = { bug: Bug, feature: Lightbulb, improvement: MagicWand, question: Megaphone };

export default function Roadmap() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/tracker/public-roadmap");
        setItems(r.data.items || []);
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  const buckets = {
    in_progress: items.filter((i) => i.status === "in_progress"),
    triaged: items.filter((i) => i.status === "triaged"),
    new: items.filter((i) => i.status === "new"),
    resolved: items.filter((i) => i.status === "resolved" || i.status === "verified"),
  };

  return (
    <AppShell title="Roadmap">
      <div className="max-w-6xl mx-auto" data-testid="roadmap-page">
        <div className="mb-8">
          <h1 className="text-3xl font-display font-semibold text-zinc-900">Product Roadmap</h1>
          <p className="text-sm text-zinc-600 mt-2">What we&apos;re building next, what&apos;s in flight, and what we&apos;ve recently shipped. Got a request? Hit the <strong>Report</strong> button on any page.</p>
        </div>
        {loading ? (
          <Card className="p-6 text-sm text-zinc-500">Loading…</Card>
        ) : items.length === 0 ? (
          <Card className="p-10 text-center" data-testid="empty-roadmap">
            <Megaphone size={36} weight="duotone" className="mx-auto text-zinc-400" />
            <div className="font-medium text-zinc-700 mt-3">Nothing publicly on the roadmap yet</div>
            <div className="text-sm text-zinc-500 mt-1">Admins haven&apos;t flagged any tracker items as public yet.</div>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Column title="Building now" items={buckets.in_progress} accent="amber" />
            <Column title="Planned" items={buckets.triaged} accent="indigo" />
            <Column title="Under consideration" items={buckets.new} accent="zinc" />
            <Column title="Recently shipped" items={buckets.resolved} accent="emerald" />
          </div>
        )}
      </div>
    </AppShell>
  );
}

function Column({ title, items, accent }) {
  return (
    <div data-testid={`column-${title.replace(/\s/g, "-")}`}>
      <h2 className={`text-sm font-semibold mb-3 text-${accent}-700 flex items-center gap-2`}>
        <span className={`w-2 h-2 rounded-full bg-${accent}-500 inline-block`} /> {title}
        <span className="text-zinc-400 text-xs ml-auto">{items.length}</span>
      </h2>
      <div className="space-y-2">
        {items.length === 0 ? (
          <div className="text-xs text-zinc-400 italic px-3">Nothing here.</div>
        ) : items.map((it) => {
          const Icon = TYPE_ICONS[it.type] || Lightbulb;
          return (
            <Card key={it.id} className="p-3" data-testid={`card-${it.id}`}>
              <div className="flex items-start gap-2">
                <Icon size={18} weight="duotone" className="text-zinc-500 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-zinc-900">{it.title}</div>
                  {it.description && <div className="text-xs text-zinc-500 mt-1 line-clamp-2">{it.description}</div>}
                  <div className="text-[10px] text-zinc-400 mt-2 flex items-center gap-1">
                    <Badge variant="outline" className="text-[10px] capitalize">{(it.module || "other").replace(/_/g, " ")}</Badge>
                  </div>
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
